import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import ReactGA from "react-ga";
import { sendPageView } from "lib/googleAnalytics/page";
import log from "loglevel";
import { Container, Row, Col, Card, Alert, Button } from "react-bootstrap";
import ReactPhoneInput from "react-phone-input-2";
// import "react-phone-input-2/dist/style.css";
import ClipLoader from "react-spinners/ClipLoader";
import { override } from "components/spinner/spinnerOverride";
import queryString from "query-string";
import { Navbar } from "@avlino-inc/alenza-react";
import {
  registerAuthyUserAction,
  logoutUserAction,
} from "store/actions/actionCreators";
import {
  getAuthyRegisterReducerResponse,
  getAuthyRegisterReducerError,
} from "store/selectors";
import genStyles from "styles/General.module.scss";
import styles from "./Auth.module.scss";
import { getTheme } from "lib/theme";
import { ThemeLogo } from "components/pages/auth/ThemeLogo";
import { SuccessType, AddAuthyUserRequestType, ErrorType } from "types";
import * as routes from "routes";

type RegisterPhoneDetailsType = {
  fullPhoneNumber: string | undefined;
  countryCode: string | undefined;
  formattedPhoneNumber: string | undefined;
};

type PossibleUrlParams = {
  redirect?: string;
};

const AuthTwoFactorRegistrationPage = ({
  authed,
  tfaRegistered,
  tfaVerified,
}: {
  authed: Promise<boolean>;
  tfaRegistered: Promise<boolean>;
  tfaVerified: Promise<boolean>;
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const authyResponse: SuccessType = useSelector((state) =>
    getAuthyRegisterReducerResponse(state, "data")
  );
  const authyError: ErrorType = useSelector((state) =>
    getAuthyRegisterReducerError(state)
  );

  // eslint-disable-next-line
  const [authorized, setAuthorized] = useState<boolean>(false);
  const [registerPhone, setRegisterPhone] = useState<
    RegisterPhoneDetailsType
  >();
  const [isRegisteringPhone, setIsRegisteringPhone] = useState<boolean>(false);
  const [isUpdatingPhoneNumber, setIsUpdatingPhoneNumber] = useState<boolean>(
    false
  );
  const [theme, setTheme] = useState<string | undefined>();
  const [urlParams] = useState<PossibleUrlParams>(
    queryString.parse(location.search)
  );

  useEffect(() => {
    if (authed && tfaRegistered) {
      authed.then((allow) => {
        if (allow) {
          tfaVerified.then((isAlreadyVerified) => {
            if (isAlreadyVerified) {
              theme === "aicon"
                ? history.push(routes.AICON_DUMMY_ROUTE)
                : history.push(routes.DASHBOARD_MAIN);
            } else {
              tfaRegistered.then((isRegistered) => {
                if (isRegistered) {
                  // User is already registered, push to verification
                  history.push(routes.TWOFACTOR_VERIFY);
                } else {
                  // Stay on page
                  setAuthorized(true);
                  !theme
                    ? setTheme(getTheme())
                    : log.info("Theme already set.");
                }
              });
            }
          });
        } else {
          history.push(routes.LOGIN);
        }
      });
    } else {
      history.push(routes.LOGIN);
    }
  }, [authed, tfaRegistered, tfaVerified, history, theme]);

  useEffect(() => {
    if (authyResponse && authyResponse.success) {
      history.push(routes.TWOFACTOR_VERIFY);
    }
  }, [authyResponse, history]);

    /**
   * Google Analytics
   */
  useEffect(() => {
    sendPageView(location.pathname + location.search);
  }, [location]);
  
  const onHandleLogout = (event: any) => {
    event.preventDefault();
    dispatch(logoutUserAction({}));
    history.push(routes.LOGIN);
  };

  const onHandleAuthyRegister = (event: any) => {
    event.preventDefault();
    if (
      registerPhone &&
      registerPhone?.fullPhoneNumber &&
      registerPhone?.countryCode &&
      registerPhone?.formattedPhoneNumber
    ) {
      log.info("All fields are valid! Creating/Resetting Password ...");
      const request: AddAuthyUserRequestType = {
        body: {
          country_code: registerPhone.countryCode,
          phone_number: registerPhone.formattedPhoneNumber,
        },
      };
      log.info("-- Dispatching registerAuthyUserAction()...");
      setIsRegisteringPhone(true);
      dispatch(registerAuthyUserAction(request));
    } else {
      log.info(
        "One or more fields NOT VALID! Will not register two-factor. " +
          JSON.stringify(registerPhone)
      );
    }
  };

  const handlePhoneNumberChange = (value: any, data: any, event: any) => {
    setRegisterPhone({
      fullPhoneNumber: value,
      countryCode: data.dialCode,
      formattedPhoneNumber: value
        .replace(/[^0-9]+/g, "")
        .slice(data.dialCode.length),
    });
    setIsUpdatingPhoneNumber(true);
  };

  const badRegisterAlert = (data: SuccessType, error: ErrorType) => {
    if (data && data.success) {
      log.info("Successful Authy Verification!");
    } else if (error) {
      log.info("Unsuccessful Login.");
      return <Alert variant="danger">Unable to complete registration</Alert>;
    }
    return null;
  };

  const onClickUserCard = useCallback(() => history.push(routes.LOGIN), []);

  return (
    <div id="AuthTwoFactorRegistrationPage" className={styles["auth-page"]}>
      <Navbar
					showLogo
					logoUrl='/'
          productDefinition = {
            {
              productName:'Alenza'
            }
          }
					productNameUrl='/'
					dropdown={undefined}
					notificationItems={undefined}
          onClickUserCard={onClickUserCard}
          email={""}
      />
      
      <Container className={styles["auth-page__container"]} fluid={true}>
        <Row className={styles["auth-page__container__row"]}>
          <Col md={12} className={styles["auth-page__container__row__content"]}>
            <Card
              className={
                styles["auth-page__container__row__content__auth-card"]
              }
            >
              <Card.Body>
                <div
                  className={
                    styles[
                      "auth-page__container__row__content__auth-card__auth-logo"
                    ]
                  }
                >
                  <ThemeLogo theme={theme} />
                </div>
                <div>
                  <Card.Title>Two-factor Registration</Card.Title>
                  {!isUpdatingPhoneNumber
                    ? badRegisterAlert(authyResponse, authyError)
                    : null}
                  <form>
                    <div style={{ marginTop: "40px" }}>
                      <ReactPhoneInput
                        {
                          ...{
                            inputExtraProps: {
                              name: "full_phone_number",
                              required: true,
                              autoFocus: true,
                            },
                            defaultCountry: "us",
                            disableAreaCodes:true,
                            enableSearchField:true
                          } as {}
                        }
                        searchPlaceholder={"Search"}
                        preferredCountries={["us", "gb", "ph"]}
                        countryCodeEditable={false}
                        value={registerPhone?.fullPhoneNumber}
                        onChange={handlePhoneNumberChange}
                        disableSearchIcon={true}
                        inputClass={genStyles["alenza__input"]}
                        buttonClass={
                          styles[
                            "auth-page__container__row__content__auth-card__flag-dropdown"
                          ]
                        }
                      />
                    </div>
                    <Button
                      type="submit"
                      className={genStyles["alenza__btn-primary"]}
                      center-block="true"
                      disabled={isRegisteringPhone}
                      onClick={(e: any) => {
                        onHandleAuthyRegister(e);
                      }}
                    >
                      {isRegisteringPhone ? (
                        <ClipLoader
                          color={"#ffffff"}
                          css={override}
                          size={14}
                          loading={isRegisteringPhone}
                        />
                      ) : (
                        <>Register</>
                      )}
                    </Button>
                  </form>
                </div>
              </Card.Body>
              <Card.Footer>
                <div className={genStyles["link"]} onClick={onHandleLogout}>
                  Sign in as another user
                </div>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AuthTwoFactorRegistrationPage;
